import { useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { formatISO, isSameDay, parseISO } from 'date-fns'
import { usePrompt, useToast } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useConfirm, usePermissions } from '@/utils/hooks'
import type { AddonKind } from '../../hooks/useEnabledAddonKinds'
import { useEnabledAddonKinds } from '../../hooks/useEnabledAddonKinds'
import { PERMISSIONS } from '@/utils/permissions'
import { getNodes } from '@/utils/graphql'
import { format } from '@/utils/date-fns'
import {
  ALL_FIXED_ADDONS,
  ALL_TIME_ADDONS,
  ALL_TIME_OVERRIDES,
} from '../../queries'
import {
  CREATE_FIXED_ADDON,
  CREATE_TIME_ADDON,
  CREATE_TIME_OVERRIDE,
  DELETE_FIXED_ADDON,
  DELETE_TIME_ADDON,
  DELETE_TIME_OVERRIDE,
  PATCH_FIXED_ADDON,
  PATCH_TIME_ADDON,
  PATCH_TIME_OVERRIDE,
} from '../../mutations'
import { EditAddonPrompt } from '../../EditAddonPrompt'
import { EditFixedAddonPrompt } from '../../EditFixedAddonPrompt'
import type { FixedAddonForm, OverrideOrAddonForm } from '../../types'

export type WorkPeriodAddonKind = AddonKind

export interface WorkPeriodAddon {
  id: string
  kind: WorkPeriodAddonKind
  salaryCodeName: string
  /** Compact value for the chip: a time span, or the fixed amount. */
  value: string
  comment: string
  /** Sorts the three kinds into one list. */
  sortKey: number
}

interface Options {
  userId: string
  datetimeStart: Date
  datetimeEnd: Date
  /**
   * The lists are only fetched once the approver opens the popover — a month
   * of work periods would otherwise fire three queries per row on load.
   */
  enabled: boolean
  onChange?: () => void
}

const REFETCH_QUERIES = [
  'AllTimeAddons',
  'AllTimeOverrides',
  'AllFixedAddons',
  'DriverActivityPeriodReport',
  'DriverActivityPeriodReportShallow',
  'AllDynamicColumnsQuery',
  'DriverActivityDayReport',
]

interface AddonActions {
  view: boolean
  add: boolean
  change: boolean
  delete: boolean
}

const NO_ACTIONS: AddonActions = {
  view: false,
  add: false,
  change: false,
  delete: false,
}

/**
 * A kind the salary work time has turned off is hidden entirely — not listed,
 * not editable, not addable — whatever the user's permissions allow.
 */
function whenKindEnabled(kindIsEnabled: boolean, actions: AddonActions) {
  return kindIsEnabled ? actions : NO_ACTIONS
}

function formatSpan(start: Date, end: Date) {
  return isSameDay(start, end)
    ? `${format(start, 'HH:mm')}–${format(end, 'HH:mm')}`
    : `${format(start, 'dd.MM HH:mm')}–${format(end, 'dd.MM HH:mm')}`
}

export function useWorkPeriodAddons({
  userId,
  datetimeStart,
  datetimeEnd,
  enabled,
  onChange,
}: Options) {
  const translations = useTranslate({
    deleteTimeAddon: 'salary.delete-time-addon',
    deleteTimeAddonConfirm: 'salary.delete-time-addon-confirm',
    deleteFixedAddon: 'salary.delete-fixed-addon',
    deleteFixedAddonConfirm: 'salary.delete-fixed-addon-confirm',
    deleteTimeOverride: 'salary.delete-time-override',
    deleteTimeOverrideConfirm: 'salary.delete-time-override-confirm',
    toast: {
      timeCreate: 'salary.time-addon-create-success',
      timeUpdate: 'salary.time-addon-update-success',
      timeDelete: 'salary.time-addon-delete-success',
      fixedCreate: 'salary.fixed-addon-create-success',
      fixedUpdate: 'salary.fixed-addon-update-success',
      fixedDelete: 'salary.fixed-addon-delete-success',
      overrideCreate: 'salary.time-override-create-success',
      overrideUpdate: 'salary.time-override-update-success',
      overrideDelete: 'salary.time-override-delete-success',
    },
  })

  const addPrompt = usePrompt()
  const addToast = useToast()
  const confirm = useConfirm()
  const { hasPermissions } = usePermissions()

  const kindEnabled = useEnabledAddonKinds(userId, { enabled })

  const can = {
    time: whenKindEnabled(kindEnabled.time, {
      view: hasPermissions(PERMISSIONS.salary.view.timeaddon),
      add: hasPermissions(PERMISSIONS.salary.add.timeaddon),
      change: hasPermissions(PERMISSIONS.salary.change.timeaddon),
      delete: hasPermissions(PERMISSIONS.salary.delete.timeaddon),
    }),
    fixed: whenKindEnabled(kindEnabled.fixed, {
      view: hasPermissions(PERMISSIONS.salary.view.fixedaddon),
      add: hasPermissions(PERMISSIONS.salary.add.fixedaddon),
      change: hasPermissions(PERMISSIONS.salary.change.fixedaddon),
      delete: hasPermissions(PERMISSIONS.salary.delete.fixedaddon),
    }),
    override: whenKindEnabled(kindEnabled.override, {
      view: hasPermissions(PERMISSIONS.salary.view.timeoverride),
      add: hasPermissions(PERMISSIONS.salary.add.timeoverride),
      change: hasPermissions(PERMISSIONS.salary.change.timeoverride),
      delete: hasPermissions(PERMISSIONS.salary.delete.timeoverride),
    }),
  } satisfies Record<WorkPeriodAddonKind, AddonActions>

  const variables = {
    dateStart: formatISO(datetimeStart),
    dateEnd: formatISO(datetimeEnd),
  }

  const timeAddonsQuery = useQuery(ALL_TIME_ADDONS, {
    variables: { ...variables, user: userId },
    skip: !enabled || !can.time.view,
  })
  const overridesQuery = useQuery(ALL_TIME_OVERRIDES, {
    variables: { ...variables, user: userId },
    skip: !enabled || !can.override.view,
  })
  const fixedAddonsQuery = useQuery(ALL_FIXED_ADDONS, {
    variables: { ...variables, userId },
    skip: !enabled || !can.fixed.view,
  })

  // The popover's own three lists are among the refetched queries, so the rows
  // are already up to date by the time the mutation resolves.
  const mutationOptions = {
    refetchQueries: REFETCH_QUERIES,
    awaitRefetchQueries: true,
  }
  const [createTimeAddon] = useMutation(CREATE_TIME_ADDON, mutationOptions)
  const [patchTimeAddon] = useMutation(PATCH_TIME_ADDON, mutationOptions)
  const [deleteTimeAddon] = useMutation(DELETE_TIME_ADDON, mutationOptions)
  const [createTimeOverride] = useMutation(
    CREATE_TIME_OVERRIDE,
    mutationOptions
  )
  const [patchTimeOverride] = useMutation(PATCH_TIME_OVERRIDE, mutationOptions)
  const [deleteTimeOverride] = useMutation(
    DELETE_TIME_OVERRIDE,
    mutationOptions
  )
  const [createFixedAddon] = useMutation(CREATE_FIXED_ADDON, mutationOptions)
  const [patchFixedAddon] = useMutation(PATCH_FIXED_ADDON, mutationOptions)
  const [deleteFixedAddon] = useMutation(DELETE_FIXED_ADDON, mutationOptions)

  const [mutating, setMutating] = useState(false)

  const timeAddons = getNodes(timeAddonsQuery.data?.allTimeAddons)
  const overrides = getNodes(overridesQuery.data?.allTimeOverrides)
  const fixedAddons = getNodes(fixedAddonsQuery.data?.allFixedAddons)

  const addons: WorkPeriodAddon[] = [
    ...timeAddons.map(addon => ({
      id: addon.id,
      kind: 'time' as const,
      salaryCodeName: addon.salaryCode?.name ?? '',
      value: formatSpan(
        parseISO(addon.datetimeStart),
        parseISO(addon.datetimeEnd)
      ),
      comment: addon.comment ?? '',
      sortKey: parseISO(addon.datetimeStart).getTime(),
    })),
    ...overrides.map(override => ({
      id: override.id,
      kind: 'override' as const,
      salaryCodeName: override.salaryCode?.name ?? '',
      value: formatSpan(
        parseISO(override.datetimeStart),
        parseISO(override.datetimeEnd)
      ),
      comment: override.comment ?? '',
      sortKey: parseISO(override.datetimeStart).getTime(),
    })),
    ...fixedAddons.map(addon => ({
      id: addon.id,
      kind: 'fixed' as const,
      salaryCodeName: addon.salaryCode?.name ?? '',
      value: `× ${addon.amount}`,
      comment: addon.comment ?? '',
      // Fixed addons carry a date, not a time, so they trail the day's spans.
      sortKey: Number.MAX_SAFE_INTEGER,
    })),
  ].sort((a, b) => a.sortKey - b.sortKey)

  const loading =
    timeAddonsQuery.loading ||
    overridesQuery.loading ||
    fixedAddonsQuery.loading

  /** True once the three lists have actually been read at least once. */
  const loaded =
    enabled &&
    !loading &&
    (timeAddonsQuery.called ||
      overridesQuery.called ||
      fixedAddonsQuery.called) &&
    !timeAddonsQuery.error &&
    !overridesQuery.error &&
    !fixedAddonsQuery.error

  async function promptTimeForm(
    initialData: OverrideOrAddonForm,
    options: { isOverride?: boolean; addonId?: string } = {}
  ) {
    const { data } = await addPrompt<OverrideOrAddonForm | null>(resolve => (
      <EditAddonPrompt
        initialData={initialData}
        isOverride={options.isOverride}
        addonId={options.addonId}
        userId={userId}
        onSubmit={resolve}
      />
    ))
    if (
      !data ||
      !data.datetimeStart ||
      !data.datetimeEnd ||
      !data.salaryCode ||
      typeof data.comment === 'undefined'
    )
      return null
    return {
      datetimeStart: formatISO(data.datetimeStart),
      datetimeEnd: formatISO(data.datetimeEnd),
      salaryCode: data.salaryCode,
      salaryCodeIfOvertime: data.salaryCodeIfOvertime,
      overrideOtherAddons: data.overrideOtherAddons ?? false,
      comment: data.comment,
    }
  }

  async function promptFixedForm(initialData: FixedAddonForm) {
    const { data } = await addPrompt<FixedAddonForm | null>(resolve => (
      <EditFixedAddonPrompt
        initialData={initialData}
        userId={userId}
        onSubmit={resolve}
      />
    ))
    if (!data || !data.salaryCode) return null
    return {
      date: format(data.date, 'yyyy-MM-dd'),
      salaryCode: data.salaryCode,
      amount: data.amount,
      comment: data.comment,
    }
  }

  async function runMutation(mutate: () => Promise<unknown>, toast: string) {
    setMutating(true)
    try {
      await mutate()
      addToast('success', toast)
      onChange?.()
    } finally {
      setMutating(false)
    }
  }

  async function add(kind: WorkPeriodAddonKind) {
    if (kind === 'fixed') {
      const data = await promptFixedForm({
        date: datetimeStart,
        salaryCode: null,
        amount: 1,
        comment: '',
      })
      if (!data) return
      await runMutation(
        () =>
          createFixedAddon({
            variables: { input: { user: userId, ...data } },
          }),
        translations.toast.fixedCreate
      )
      return
    }

    const isOverride = kind === 'override'
    const data = await promptTimeForm(
      {
        datetimeStart,
        datetimeEnd,
        salaryCode: null,
        salaryCodeIfOvertime: null,
        overrideOtherAddons: false,
        comment: '',
      },
      { isOverride }
    )
    if (!data) return

    const { overrideOtherAddons, salaryCodeIfOvertime, ...shared } = data

    await runMutation(
      () =>
        isOverride
          ? createTimeOverride({
              variables: { input: { user: userId, ...shared } },
            })
          : createTimeAddon({
              variables: {
                input: {
                  user: userId,
                  ...shared,
                  salaryCodeIfOvertime,
                  overrideOtherAddons,
                },
              },
            }),
      isOverride
        ? translations.toast.overrideCreate
        : translations.toast.timeCreate
    )
  }

  async function edit(addon: WorkPeriodAddon) {
    if (addon.kind === 'fixed') {
      const existing = fixedAddons.find(item => item.id === addon.id)
      if (!existing) return
      const data = await promptFixedForm({
        date: existing.date ? parseISO(existing.date) : datetimeStart,
        salaryCode: existing.salaryCode?.id ?? '',
        amount: existing.amount,
        comment: existing.comment ?? '',
      })
      if (!data) return
      await runMutation(
        () =>
          patchFixedAddon({
            variables: { id: addon.id, input: data },
          }),
        translations.toast.fixedUpdate
      )
      return
    }

    const isOverride = addon.kind === 'override'
    const existingOverride = overrides.find(item => item.id === addon.id)
    const existingTimeAddon = timeAddons.find(item => item.id === addon.id)
    const existing = isOverride ? existingOverride : existingTimeAddon
    if (!existing) return

    const data = await promptTimeForm(
      {
        datetimeStart: parseISO(existing.datetimeStart),
        datetimeEnd: parseISO(existing.datetimeEnd),
        salaryCode: existing.salaryCode?.id ?? '',
        salaryCodeIfOvertime:
          existingTimeAddon?.salaryCodeIfOvertime?.id ?? null,
        overrideOtherAddons: existingTimeAddon?.overrideOtherAddons ?? false,
        comment: existing.comment ?? '',
      },
      { isOverride, addonId: addon.id }
    )
    if (!data) return

    const { overrideOtherAddons, salaryCodeIfOvertime, ...shared } = data

    await runMutation(
      () =>
        isOverride
          ? patchTimeOverride({
              variables: { id: addon.id, input: shared },
            })
          : patchTimeAddon({
              variables: {
                id: addon.id,
                input: { ...shared, salaryCodeIfOvertime, overrideOtherAddons },
              },
            }),
      isOverride
        ? translations.toast.overrideUpdate
        : translations.toast.timeUpdate
    )
  }

  async function remove(addon: WorkPeriodAddon) {
    const copy = {
      time: {
        title: translations.deleteTimeAddon,
        prompt: translations.deleteTimeAddonConfirm,
        toast: translations.toast.timeDelete,
      },
      fixed: {
        title: translations.deleteFixedAddon,
        prompt: translations.deleteFixedAddonConfirm,
        toast: translations.toast.fixedDelete,
      },
      override: {
        title: translations.deleteTimeOverride,
        prompt: translations.deleteTimeOverrideConfirm,
        toast: translations.toast.overrideDelete,
      },
    }[addon.kind]

    const { data } = await confirm(copy.prompt, copy.title)
    if (!data) return

    const variables = { id: addon.id }
    await runMutation(
      () =>
        addon.kind === 'fixed'
          ? deleteFixedAddon({ variables })
          : addon.kind === 'override'
          ? deleteTimeOverride({ variables })
          : deleteTimeAddon({ variables }),
      copy.toast
    )
  }

  return {
    addons,
    loading: loading || mutating,
    loaded,
    can,
    add,
    edit,
    remove,
  }
}
